// Job descriptions kept in PME People (see backend app/services/job_descriptions.py).

export interface JDMeasure {
  text: string;
  proposed: boolean;
}

export interface JDKra {
  area: string;
  weighting: number | null;
  covers: string;
  measured_by: string;
  tasks: string[];
  measures: JDMeasure[];
}

export interface JDCompetency {
  name: string;
  description: string;
}

export type JDDetailKey =
  | "name"
  | "job_title"
  | "department"
  | "reports_to"
  | "direct_reports"
  | "qualifications"
  | "scope"
  | "hr_contact"
  | "date_last_reviewed";

export interface JDContent {
  details: Record<JDDetailKey, string>;
  purpose: string;
  kras: JDKra[];
  communication: { covers: string; measured_by: string };
  competencies: JDCompetency[];
  proposed_note: string;
}

export type JDStatus = "draft" | "issued" | "changes_requested" | "signed" | "superseded";

export interface JDVersion {
  id: number;
  employee_id: number;
  version: number;
  status: JDStatus;
  source: string;
  source_document_id: number | null;
  import_notes: string[];
  summary: string;
  issued_at: string | null;
  issued_by_name: string | null;
  approved_by_name: string | null;
  last_emailed_at: string | null;
  signed_at: string | null;
  signature_type: string | null;
  signature_reference: string | null;
  signed_document_id: number | null;
  change_comment: string | null;
  change_requested_at: string | null;
  superseded_at: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
  weighting_total: number;
  /** Recorded from a file the person had already signed in PME People. */
  signed_on_upload?: boolean;
  /** The uploaded file this version was read from. */
  source_document?: JDSourceDocument | null;
  content?: JDContent;
  problems?: string[];
}

export interface JDSourceDocument {
  id: number;
  title: string;
  file_type: string;
  uploaded_at: string | null;
  signed_at: string | null;
  /** Signed in PME People by the person the job description is for. */
  signed_by_employee: boolean;
  is_active: boolean;
}

export interface JDImportable {
  id: number;
  title: string;
  file_type: string;
  category: string | null;
  signed: boolean;
  requires_signature: boolean;
  uploaded_at: string | null;
  looks_like_jd: boolean;
}

export interface JDPage {
  employee: {
    id: number;
    name: string;
    job_title: string | null;
    site_name: string | null;
    manager_name: string | null;
    email: string | null;
  };
  is_self: boolean;
  can_edit: boolean;
  current: JDVersion | null;
  draft: JDVersion | null;
  history: JDVersion[];
  importable: JDImportable[];
}

export const DETAIL_FIELDS: { key: JDDetailKey; label: string; optional?: boolean; hint?: string }[] = [
  { key: "name", label: "Name" },
  { key: "job_title", label: "Job title" },
  { key: "department", label: "Department" },
  { key: "reports_to", label: "Reports to", hint: "Line manager's name and job title" },
  { key: "direct_reports", label: "Direct reports", optional: true, hint: "Names or the number of people" },
  { key: "qualifications", label: "Qualifications", optional: true, hint: "Required or preferred" },
  { key: "scope", label: "Scope", hint: "Entities, regions or accounts covered, e.g. PME UK, ES, US" },
  { key: "hr_contact", label: "HR contact" },
  { key: "date_last_reviewed", label: "Date last reviewed" },
];

export const PERFORMANCE_REVIEW_TEXT =
  "Performance against this job description is reviewed through the PME People appraisal, using the company " +
  "rating scale. Review dates, scoring and development plans are managed in PME People and are not part of this document.";

export const SIGN_OFF_TEXT =
  "By signing, I confirm that I have read this job description and performance standards and that it is an " +
  "accurate description of my role. If anything is wrong, please tell your manager or HR before signing.";

export function weightingTotal(content: JDContent): number {
  return content.kras.reduce((sum, k) => sum + (k.weighting ?? 0), 0);
}

export function hasProposed(content: JDContent): boolean {
  return content.kras.some((k) => k.measures.some((m) => m.proposed));
}

/** Mirrors issue_problems() on the server, so the editor can say what is left. */
export function issueProblems(content: JDContent): string[] {
  const out: string[] = [];
  if (!content.details.name.trim()) out.push("Add the employee's name.");
  if (!content.details.job_title.trim()) out.push("Add the job title.");
  if (!content.purpose.trim()) out.push("Write the job purpose (section 1).");
  const kras = content.kras.filter((k) => k.area.trim() || k.covers.trim() || k.tasks.some((t) => t.trim()) || k.measures.some((m) => m.text.trim()));
  if (kras.length === 0) out.push("Add at least one key result area (section 2).");
  kras.forEach((k, i) => {
    if (!k.area.trim()) out.push(`Key result area ${i + 1} needs a name.`);
    if (k.weighting == null) out.push(`Key result area ${i + 1} needs a weighting.`);
  });
  if (kras.length > 0 && kras.every((k) => k.weighting != null)) {
    const total = kras.reduce((s, k) => s + (k.weighting ?? 0), 0);
    if (total !== 100) out.push(`The weightings add up to ${total}%. They must add up to 100%.`);
  }
  if (hasProposed(content) && !content.proposed_note.trim()) {
    out.push("Some measures are marked as proposed (*). Add a note saying who will confirm them.");
  }
  const text = [
    ...Object.values(content.details),
    content.purpose,
    ...content.kras.flatMap((k) => [k.area, k.covers, k.measured_by, ...k.tasks, ...k.measures.map((m) => m.text)]),
    ...content.competencies.flatMap((c) => [c.name, c.description]),
  ].join("\n");
  const placeholder = text.match(/\[[^\]]{2,80}\]/);
  if (placeholder) out.push(`Replace the placeholder text ${placeholder[0]} from the template.`);
  return out;
}

export function emptyKra(): JDKra {
  return { area: "", weighting: null, covers: "", measured_by: "", tasks: [""], measures: [{ text: "", proposed: false }] };
}

const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });

export function jdDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : DATE_FMT.format(d);
}

export const KRA_COLOURS = ["#0872c1", "#df95ef", "#0c3c63", "#78d6ff", "#8a3a9c", "#3aa37a", "#e0a43b", "#5b6b7f"];
